import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { buildingService, type Edificio } from '@/services/buildingService';
import { unitService, type Unidad } from '@/services/unitService';
import { tenantService, type Inquilino } from '@/services/tenantService';
import { Button } from '@/components/ui/button';
import UnitForm from '@/components/UnitForm';
import TenantForm from '@/components/TenantForm';
import { toast } from 'sonner';
import Breadcrumbs from '@/components/ui/Breadcrumbs';

const BuildingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [building, setBuilding] = useState<Edificio | null>(null);
  const [units, setUnits] = useState<Unidad[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUnitFormOpen, setIsUnitFormOpen] = useState(false);
  const [currentUnit, setCurrentUnit] = useState<Unidad | null>(null);
  const [isTenantFormOpen, setIsTenantFormOpen] = useState(false);
  const [currentTenant, setCurrentTenant] = useState<Inquilino | null>(null);

  const fetchBuildingData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const fetchedBuilding = await buildingService.getBuildings().then(b => b.find(bld => bld.id_edificio === id));
      if (fetchedBuilding) {
        setBuilding(fetchedBuilding);
        const fetchedUnits = await unitService.getUnitsByBuilding(id);
        // Fetch tenant for each unit
        const unitsWithTenants = await Promise.all(fetchedUnits.map(async (unit) => {
          if (unit.esta_ocupada) {
            const tenants = await tenantService.getTenantsByUnit(unit.id_unidad!);
            return { ...unit, currentTenant: tenants[0] || null };
          }
          return { ...unit, currentTenant: null };
        }));
        setUnits(unitsWithTenants);
      } else {
        setError("Edificio no encontrado.");
      }
    } catch (err: any) {
      setError(err.message);
      toast.error("Error al cargar los datos del edificio.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBuildingData();
  }, [id]);

  const handleSaveUnit = async (unit: Unidad) => {
    try {
      if (unit.id_unidad) {
        await unitService.updateUnit(unit.id_unidad, unit);
        toast.success("Unidad actualizada exitosamente.");
      } else {
        await unitService.createUnit(unit);
        toast.success("Unidad creada exitosamente.");
      }
      fetchBuildingData();
    } catch (err: any) {
      toast.error(`Error al guardar la unidad: ${err.message}`);
    }
  };

  const handleEditUnit = (unit: Unidad) => {
    setCurrentUnit(unit);
    setIsUnitFormOpen(true);
  };

  const handleDeleteUnit = async (unitId: string) => {
    if (window.confirm("¿Estás seguro de que quieres eliminar esta unidad?")) {
      try {
        await unitService.deleteUnit(unitId);
        toast.success("Unidad eliminada exitosamente.");
        fetchBuildingData();
      } catch (err: any) {
        toast.error(`Error al eliminar la unidad: ${err.message}`);
      }
    }
  };

  const handleSaveTenant = async (tenant: Inquilino) => {
    try {
      if (tenant.id_inquilino) {
        await tenantService.updateTenant(tenant.id_inquilino, tenant);
        toast.success("Inquilino actualizado exitosamente.");
      } else {
        await tenantService.createTenant(tenant);
        toast.success("Inquilino creado exitosamente.");
      }
      fetchBuildingData();
    } catch (err: any) {
      toast.error(`Error al guardar el inquilino: ${err.message}`);
    }
  };

  const handleAddTenant = (unit: Unidad) => {
    setCurrentTenant(null);
    setCurrentUnit(unit); // Set the current unit for the tenant form
    setIsTenantFormOpen(true);
  };

  const handleEditTenant = (tenant: Inquilino) => {
    setCurrentTenant(tenant);
    setIsTenantFormOpen(true);
  };

  const breadcrumbs = [
    { label: "Edificios", path: "/buildings" },
    { label: building?.nombre_edificio || "Cargando...", path: `/buildings/${id}` },
  ];

  if (loading) {
    return <div>Cargando detalles del edificio...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!building) {
    return <div>Edificio no encontrado.</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <Breadcrumbs items={breadcrumbs} />
      <Button onClick={() => navigate(-1)} className="mb-4">Volver</Button>
      <h1 className="text-2xl font-bold mb-4">Detalles del Edificio: {building.nombre_edificio}</h1>
      <p>Dirección: {building.direccion}</p>
      <p>Número de Unidades: {building.numero_unidades}</p>

      <h2 className="text-xl font-bold mt-8 mb-4">Unidades</h2>
      <Button className="mb-4" onClick={() => { setCurrentUnit(null); setIsUnitFormOpen(true); }}>Agregar Nueva Unidad</Button>
      {units.length === 0 ? (
        <p>No hay unidades registradas para este edificio.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {units.map((unit) => (
            <div key={unit.id_unidad} className="border p-4 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold">Unidad: {unit.numero_unidad}</h3>
              <p>Tipo: {unit.tipo_unidad}</p>
              <p>Ocupada: {unit.esta_ocupada ? 'Sí' : 'No'}</p>
              {unit.esta_ocupada && unit.currentTenant && (
                <p className="text-gray-700">Inquilino: {unit.currentTenant.nombre_completo}</p>
              )}
              <div className="mt-4 flex space-x-2">
                <Button variant="secondary" onClick={() => handleEditUnit(unit)}>Editar Unidad</Button>
                <Button variant="destructive" onClick={() => handleDeleteUnit(unit.id_unidad!)}>Eliminar Unidad</Button>
                {!unit.esta_ocupada && (
                  <Button variant="outline" onClick={() => handleAddTenant(unit)}>Agregar Inquilino</Button>
                )}
                {unit.esta_ocupada && unit.currentTenant && (
                  <Button variant="outline" onClick={() => handleEditTenant(unit.currentTenant!)}>Editar Inquilino</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <UnitForm
        isOpen={isUnitFormOpen}
        onClose={() => setIsUnitFormOpen(false)}
        onSave={handleSaveUnit}
        initialData={currentUnit}
        buildingId={id!}
      />
      <TenantForm
        isOpen={isTenantFormOpen}
        onClose={() => setIsTenantFormOpen(false)}
        onSave={handleSaveTenant}
        initialData={currentTenant}
        unitId={currentUnit?.id_unidad} // Pass the unitId to the tenant form
      />
    </div>
  );
};

export default BuildingDetailPage;
